import { useEffect, useState } from 'react';
import cn from 'classnames';

import formatNumber from '@/functions/formatNumber';
import BlockLink from '@/components/BlockLink';
import { type CarbonEmissionsProps } from './CarbonEmissions.types';

const CarbonEmissions: React.FC<CarbonEmissionsProps> = ({
  title,
  showTitle = 'true',
  content,
  linkText,
  linkExternal,
  linkInternal,
  crossOriginImageWeight,
}) => {
  const [carbonWeight, setCarbonWeight] = useState(0);
  const [CO2, setCO2] = useState(0);

  useEffect(() => {
    function calculatePageWeight() {
      const resources: Array<string> = [];
      const resourceSizes: Array<number> = [];

      const { performance } = window;
      const entries = performance.getEntriesByType(
        'resource'
      ) as PerformanceResourceTiming[];

      // accumulate the sizes in bytes for all of the resources on the page
      if (entries.length > 0) {
        entries.forEach((entry) => {
          const { name, encodedBodySize } = entry;

          if (resources.includes(name) || encodedBodySize === 0) return;

          resourceSizes.push(encodedBodySize);
          resources.push(name);
        });
      }

      // calculate the total resources in bytes
      const carbonWeightBytes = resourceSizes.reduce(
        (prev, curr) => prev + curr,
        0
      );

      // convert to MB - temporarily adding extra image weight from props which
      // is the total weight of images loaded from another domain (AWS S3), as
      // we cannot currently send back required Timing-Allow-Origin header
      // easily, so these are being returned by the performance API as having
      // an encodedBodySize of 0.
      const carbonWeightMB =
        carbonWeightBytes / 1000 / 1000 + crossOriginImageWeight;

      // calculate kWh required to serve the total resources
      const basekWh = 1.8;
      const kWh = (carbonWeightMB / 1000) * basekWh;

      // international standard 475g/kWh
      const basegkWh = 475;

      setCarbonWeight(carbonWeightMB);
      setCO2(kWh * basegkWh);
    }

    // in case the load event has already fired before this hook
    if (document.readyState === 'complete') {
      calculatePageWeight();
    } else {
      // wait until all resources are loaded before calculating
      window.addEventListener('load', calculatePageWeight);

      return () => window.removeEventListener('load', calculatePageWeight);
    }
  }, []);

  return (
    <div className="carbon-emissions-block">
      <div className="carbon-emissions-block__main" data-aos="fade-in">
        {title && showTitle && (
          <h2 className="carbon-emissions-block__heading h4">{title}</h2>
        )}
        <div
          className="carbon-emissions-block__content"
          dangerouslySetInnerHTML={{ __html: content }}
        />
        <BlockLink
          linkText={linkText}
          linkExternal={linkExternal}
          linkInternal={linkInternal}
          buttonStyle="primary-alt"
          arrowFill="var(--color-indigo)"
          extraClasses="carbon-emissions-block__link carbon-emissions-block__cta"
        />
      </div>
      <div className="carbon-emissions-block__widget" data-aos="fade-in">
        <div className="carbon-emissions">
          <table className="carbon-emissions__table">
            <thead>
              <tr>
                <th className="carbon-emissions__table-header" />
                <th className="carbon-emissions__table-header">Page weight</th>
                <th className="carbon-emissions__table-header">CO2/pageview</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td
                  className={cn(
                    'carbon-emissions__table-cell',
                    'carbon-emissions__table-cell--bold',
                    'carbon-emissions__table-cell--pad'
                  )}
                >
                  This pageview
                </td>
                <td
                  className={cn(
                    'carbon-emissions__table-cell',
                    'carbon-emissions__table-cell--bold',
                    'carbon-emissions__table-cell--pad'
                  )}
                >
                  {formatNumber(carbonWeight, 'mb')}
                </td>
                <td
                  className={cn(
                    'carbon-emissions__table-cell',
                    'carbon-emissions__table-cell--bold'
                  )}
                >
                  {formatNumber(CO2, 'g')}
                </td>
              </tr>
              <tr>
                <td className="carbon-emissions__table-cell">Avg webpage</td>
                <td className="carbon-emissions__table-cell">3.17mb</td>
                <td className="carbon-emissions__table-cell">2.71g</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CarbonEmissions;
